import type {
  ApplyWorkOrderMergeRequestDto,
  AssignWorkOrderRequestDto,
  CompleteWorkOrderRequestDto,
  HoldWorkOrderRequestDto,
  MaintenanceTicketDetailDto,
  MoveTicketRequestDto,
  WorkOrderBoardResponseDto,
  WorkOrderDetailDto,
  WorkOrderMergeSuggestionsResponseDto,
} from '@evimo/client/models';
import {
  maintenanceExecutionRequestInit,
  maintenanceTicketsApi,
  workOrdersApi,
} from '~/app/services/maintenance-execution/maintenance-execution-api-instances';

export type WorkOrderBoardQuery = {
  organizationId?: string;
  includeTerminal?: boolean;
};

export type WorkOrderMergeSuggestionsQuery = {
  organizationId?: string;
  page?: number;
  limit?: number;
  siteId?: string;
  isoWeekKey?: string;
  scheduledFrom?: string;
  scheduledTo?: string;
  status?: Array<'PLANNED' | 'ASSIGNED'>;
};

export const MaintenanceExecutionService = {
  getBoard(
    query: WorkOrderBoardQuery = {},
  ): Promise<WorkOrderBoardResponseDto> {
    return workOrdersApi.workOrderControllerGetBoard(
      {
        includeTerminal: query.includeTerminal,
        organizationId: query.organizationId,
      },
      maintenanceExecutionRequestInit,
    );
  },

  getMergeSuggestions(
    query: WorkOrderMergeSuggestionsQuery = {},
  ): Promise<WorkOrderMergeSuggestionsResponseDto> {
    return workOrdersApi.workOrderControllerGetMergeSuggestions(
      {
        organizationId: query.organizationId,
        page: query.page,
        limit: query.limit,
        siteId: query.siteId,
        isoWeekKey: query.isoWeekKey,
        scheduledFrom: query.scheduledFrom,
        scheduledTo: query.scheduledTo,
        status: query.status,
      },
      maintenanceExecutionRequestInit,
    );
  },

  applyMerge(
    body: ApplyWorkOrderMergeRequestDto,
    organizationId?: string,
  ): Promise<void> {
    return workOrdersApi.workOrderControllerApplyMerge(
      {
        applyWorkOrderMergeRequestDto: body,
        organizationId,
      },
      maintenanceExecutionRequestInit,
    );
  },

  getWorkOrder(
    id: string,
    organizationId?: string,
  ): Promise<WorkOrderDetailDto> {
    return workOrdersApi.workOrderControllerGetById(
      { id, organizationId },
      maintenanceExecutionRequestInit,
    );
  },

  getTicket(
    id: string,
    organizationId?: string,
  ): Promise<MaintenanceTicketDetailDto> {
    return maintenanceTicketsApi.maintenanceTicketControllerGetById(
      { id, organizationId },
      maintenanceExecutionRequestInit,
    );
  },

  assign(
    id: string,
    body: AssignWorkOrderRequestDto,
    organizationId?: string,
  ): Promise<void> {
    return workOrdersApi.workOrderControllerAssign(
      { id, assignWorkOrderRequestDto: body, organizationId },
      maintenanceExecutionRequestInit,
    );
  },

  start(id: string, organizationId?: string): Promise<void> {
    return workOrdersApi.workOrderControllerStart(
      { id, organizationId },
      maintenanceExecutionRequestInit,
    );
  },

  hold(
    id: string,
    body: HoldWorkOrderRequestDto,
    organizationId?: string,
  ): Promise<void> {
    return workOrdersApi.workOrderControllerHold(
      { id, holdWorkOrderRequestDto: body, organizationId },
      maintenanceExecutionRequestInit,
    );
  },

  complete(
    id: string,
    body: CompleteWorkOrderRequestDto,
    organizationId?: string,
  ): Promise<void> {
    return workOrdersApi
      .workOrderControllerComplete(
        { id, completeWorkOrderRequestDto: body, organizationId },
        maintenanceExecutionRequestInit,
      )
      .then(() => undefined);
  },

  closeWorkOrder(workOrderId: string, organizationId?: string): Promise<void> {
    return workOrdersApi
      .workOrderControllerCloseWorkOrder(
        {
          id: workOrderId,
          organizationId: organizationId?.trim() || undefined,
        },
        maintenanceExecutionRequestInit,
      )
      .then(() => undefined);
  },

  closeIntervention(
    workOrderId: string,
    ticketId: string,
    organizationId?: string,
  ): Promise<void> {
    return workOrdersApi
      .workOrderControllerCloseIntervention(
        {
          id: workOrderId,
          ticketId,
          organizationId,
        },
        maintenanceExecutionRequestInit,
      )
      .then(() => undefined);
  },

  moveTicket(
    workOrderId: string,
    ticketId: string,
    body: MoveTicketRequestDto,
    organizationId?: string,
  ): Promise<void> {
    return workOrdersApi.workOrderControllerMoveTicket(
      {
        id: workOrderId,
        ticketId,
        moveTicketRequestDto: body,
        organizationId,
      },
      maintenanceExecutionRequestInit,
    );
  },
};
